// src/context/brandVoice/advancedModel.js — which parts of the Brand Voice screen are BASE and which "Avanzate".
// PURE: no React, no network, no copy.
//
// v199 (decisions agreed 2026-10-04). Every field is read by content generation, so the split is about effort and
// understanding, not importance. ⛔ Hiding never relaxes a rule and never loses data: the checks still cover every
// value.
//   • document fields: the three bucket_d "avoid" lists (+ Differenziatori, USP, UVP) are advanced;
//   • columns: none since v200 (the preferred-term note and the claims' `source` are base; the claims' `as_of` is
//     never shown, `ClaimsEditor` keeps it on every edit);
//   • voice: NOT part of the split (revised 2026-10-06) — the Personalità tab always shows every axis and both
//     languages, never the name; `voiceLabelForSave` fills the name in.
//   • v200: a DOCUMENT tab with nothing advanced (Principi, Fatti) shows no toggle (DocFields draws it only after
//     base rows that are followed by advanced ones);
//   • v200: English is no longer an "Avanzate" concept: every save translates (no "Aggiorna anche l'inglese"), and
//     the English state shows in every view.
import { voiceLangOf } from './voiceModel.js';

/** Advanced document fields besides the bucket_d lists. */
export const ADVANCED_DOC_PATHS = ['messaging.key_differentiators', 'messaging.usp', 'messaging.uvp'];

/** True for a document field shown only with Avanzate on. */
export function isAdvancedField(field) {
  if (!field) return false;
  return field.policy === 'bucket_d' || ADVANCED_DOC_PATHS.includes(field.field_path);
}

/**
 * A problem key that names something hidden in base view. ⭐ The voice is NEVER hidden (revised 2026-10-06): the
 * Personalità tab has no Avanzate part — every axis and both languages are always shown, and the voice name is
 * never shown at all (it is filled in by `voiceLabelForSave`) — so only document fields can be hidden.
 */
export function isHiddenProblemKey(key, fields) {
  if (voiceLangOf(key)) return false;
  const field = (fields || []).find((f) => f.field_path === key);
  return isAdvancedField(field);
}

/**
 * How many hidden things need attention while Avanzate is off: hidden document fields with a problem, an unsaved
 * change or a conflict, each counted once.
 */
export function hiddenAttention({ fields = [], problems = {}, dirty = new Set(), conflicts = new Set() }) {
  return (fields || []).filter(isAdvancedField).filter((f) => {
    const p = f.field_path;
    return (problems[p] && problems[p].length > 0) || dirty.has(p) || conflicts.has(p);
  }).length;
}

/**
 * The voice name a PUT sends. The name is never shown (a workspace has ONE personality) yet the backend requires
 * it, so it is filled in: an EMPTY name, or — on a voice still read from the platform (`read.source === 'global'`) —
 * the platform's own name, becomes `fallback` ("<workspace> — voce" / "— voice"); an existing name is kept.
 */
export function voiceLabelForSave(label, read, fallback) {
  if (!fallback) return label;
  const trim = (v) => (typeof v === 'string' ? v.trim() : '');
  const current = trim(label);
  if (current === '') return fallback;
  if (read && read.source === 'global' && current === trim(read.label)) return fallback;
  return label;
}
